import type { TFunction } from 'i18next';
import type { Path } from 'react-hook-form';
import { number } from 'yup';
import { emptyStringToNull } from '../emptyStringToNullable';
import { FLOAT_NUMBER_WITH_MAX_ONE_DECIMAL } from '../validationRegexes';

type CreatePercentFieldSchemaParameters<TFormData> = {
  field: Path<TFormData>;
  labels: Record<string, string>;
  precision?: { digits: number; regex: RegExp };
  t: TFunction;
};

export const createPercentFieldSchema = <TFormData>({
  field,
  labels,
  precision = { digits: 1, regex: FLOAT_NUMBER_WITH_MAX_ONE_DECIMAL },
  t,
}: CreatePercentFieldSchemaParameters<TFormData>) => {
  return number()
    .nullable()
    .transform(emptyStringToNull)
    .typeError(t('{field} has to be a number', { field: labels[field] }))
    .min(0, t('{field} must be greater than or equal to 0%', { field: labels[field] }))
    .max(99.9, t('{field} must be less than or equal to 99.9%', { field: labels[field] }))
    .test({
      message: t('{field} may contain only {count} {description} after decimal point', {
        count: precision.digits,
        description: precision.digits === 1 ? t('digit') : t('digits'),
        field: labels[field],
      }),
      name: 'maxOneDigitAfterDecimal',
      test: value => precision.regex.test(value?.toString() ?? '0'),
    });
};
